import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Network, RefreshCw, Search, Server, Wifi, WifiOff, CheckSquare, Square, LoaderCircle, AlertCircle, CheckCircle, ChevronDown, Trash2, X, Star, Save, SlidersHorizontal, Rows3, Sparkles } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useNavigate } from 'react-router-dom';
import { MeshDevicePanel } from '../components/meshcentral/MeshDevicePanel';
import { MeshBulkConfiguration } from '../components/meshcentral/MeshBulkConfiguration';
import { Button } from '../components/ui/button';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { StatusBadge } from '../components/ui/status-badge';
import { Select } from '../components/ui/select';
import { Modal } from '../components/ui/modal';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { meshDevicePath } from '../lib/meshcentral';
import { MeshDeviceDto } from '../models';
import { getApiError, getMeshCentralDevices, refreshMeshCentralInventory, createMeshCentralCommandJob, createMeshCentralPowerOperation, createSupportJob, runMeshCentralDiagnostics, deleteMeshCentralCommandJobs, deleteSupportJobs } from '../services/api';

type StatusFilter = 'all' | 'online' | 'offline';
type QuickFilter = 'attention' | 'stale' | 'outdated' | 'low_disk' | 'printer' | 'api' | 'active_exam' | 'update_failed';
interface SavedMeshView { id: string; name: string; query: string; status: StatusFilter; group: string; quickFilters: QuickFilter[] }
const PAGE_SIZE = 50;
const FAVORITES_KEY = 'telemed_meshcentral_favorites';
const SAVED_VIEWS_KEY = 'telemed_meshcentral_saved_views';

const QUICK_FILTERS: { id: QuickFilter; label: string }[] = [
  { id: 'attention', label: 'Требуют внимания' },
  { id: 'stale', label: 'Давно не были в сети' },
  { id: 'outdated', label: 'Версия ниже новейшей' },
  { id: 'low_disk', label: 'Мало места' },
  { id: 'printer', label: 'Ошибка принтера' },
  { id: 'api', label: 'Нет heartbeat API' },
  { id: 'active_exam', label: 'Есть незавершённый осмотр' },
  { id: 'update_failed', label: 'Ошибка обновления' },
];

function readStoredSet(key: string) {
  try { return new Set<string>(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return new Set<string>(); }
}

function readSavedViews() {
  try { return JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY) || '[]') as SavedMeshView[]; } catch { return []; }
}

function versionParts(value?: string | null) {
  return String(value || '').match(/\d+/g)?.map(Number) || [];
}

function compareVersions(left?: string | null, right?: string | null) {
  const a = versionParts(left); const b = versionParts(right);
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = (a[index] || 0) - (b[index] || 0);
    if (difference) return difference;
  }
  return 0;
}

type BulkAction =
  | 'update_medscreener'
  | 'reboot'
  | 'collect_support_bundle'
  | 'terminal_recovery'
  | 'diagnostics'
  | 'configuration_template';

interface TerminalJobState {
  nodeId: string;
  action: BulkAction;
  status: 'pending' | 'running' | 'completed' | 'failed';
  jobId?: number;
  error?: string;
  progress?: number;
}

const BULK_ACTIONS: { value: BulkAction; label: string; requiresOnline: boolean; requiresConfirmation: boolean; description: string }[] = [
  { value: 'update_medscreener', label: 'Обновить Medscreener', requiresOnline: true, requiresConfirmation: true, description: 'Скачать, бэкап, установить, health-check, авто-rollback' },
  { value: 'reboot', label: 'Перезагрузить терминал', requiresOnline: true, requiresConfirmation: true, description: 'Корректная перезагрузка устройства через MeshCentral' },
  { value: 'collect_support_bundle', label: 'Собрать пакет поддержки', requiresOnline: true, requiresConfirmation: false, description: 'Диагностика, конфиг, логи, manifest с SHA-256' },
  { value: 'terminal_recovery', label: 'Терминал не работает', requiresOnline: false, requiresConfirmation: false, description: 'Проверить состояние, разбудить, восстановить Medscreener' },
  { value: 'diagnostics', label: 'Диагностика (read-only)', requiresOnline: true, requiresConfirmation: false, description: 'RAM, диск, uptime, сеть, сервис Telemed' },
  { value: 'configuration_template', label: 'Обновить конфиг из шаблона', requiresOnline: true, requiresConfirmation: true, description: 'Сравнить полный JSON, показать важные поля, сделать backup и применить' },
];

export const MeshCentralPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<MeshDeviceDto[]>();
  const [selectedNodeId, setSelectedNodeId] = useState<string>();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [group, setGroup] = useState('all');
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [quickFilters, setQuickFilters] = useState<Set<QuickFilter>>(new Set());
  const [favorites, setFavorites] = useState<Set<string>>(() => readStoredSet(FAVORITES_KEY));
  const [savedViews, setSavedViews] = useState<SavedMeshView[]>(readSavedViews);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(true);
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);

  // Bulk operations state
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkAction>('update_medscreener');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkConfigurationOpen, setBulkConfigurationOpen] = useState(false);
  const [jobStates, setJobStates] = useState<Map<string, TerminalJobState>>(new Map());
  const [globalProgress, setGlobalProgress] = useState(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setError('');
    try {
      const meshResponse = await getMeshCentralDevices();
      setDevices(meshResponse.data.data);
    } catch (e) {
      if (!silent) setError(getApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => load(true), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  // Keep the operation dialog keyboard-friendly and prevent the page behind it from scrolling.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (bulkModalOpen) setBulkModalOpen(false);
      else if (selectedNodeId) setSelectedNodeId(undefined);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [bulkModalOpen, selectedNodeId]);

  useEffect(() => {
    if (!bulkModalOpen && !selectedNodeId) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [bulkModalOpen, selectedNodeId]);

  const refresh = async () => {
    setRefreshing(true); setError('');
    try { await refreshMeshCentralInventory(); await load(); }
    catch (e) { setError(getApiError(e)); }
    finally { setRefreshing(false); }
  };

  const groups = useMemo(() => Array.from(new Set((devices || []).map((item) => item.groupName).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'ru')), [devices]);
  const newestFleetVersion = useMemo(() => {
    const versions = (devices || []).map((item) => item.terminalVersion).filter((item): item is string => Boolean(item)).sort(compareVersions);
    return versions[versions.length - 1] || null;
  }, [devices]);
  const matchesQuickFilter = useCallback((item: MeshDeviceDto, filter: QuickFilter) => {
    const staleBoundary = Date.now() - 24 * 60 * 60 * 1000;
    const isStale = !item.online && (!item.lastSeenAt || new Date(item.lastSeenAt).getTime() < staleBoundary);
    const lowDisk = (item.diagnostics?.disk?.usedPercent || 0) >= 90;
    const apiDown = item.terminalId != null && item.telemedHeartbeatOnline === false;
    const printFailed = item.latestPrintStatus === 'failed';
    const updateFailed = item.latestUpdateStatus === 'failed';
    const appDown = item.diagnostics?.telemedStatus != null && item.diagnostics.telemedStatus !== 'running';
    if (filter === 'attention') return !item.online || isStale || lowDisk || apiDown || printFailed || updateFailed || appDown || Boolean(item.latestErrorMessage);
    if (filter === 'stale') return isStale;
    if (filter === 'outdated') return Boolean(newestFleetVersion && item.terminalVersion && compareVersions(item.terminalVersion, newestFleetVersion) < 0);
    if (filter === 'low_disk') return lowDisk;
    if (filter === 'printer') return printFailed;
    if (filter === 'api') return apiDown;
    if (filter === 'active_exam') return Boolean(item.activeExamId);
    return updateFailed;
  }, [newestFleetVersion]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (devices || [])
      .filter((item) => !needle || [item.name, item.hostname, item.ip, item.os, item.groupName, item.nodeId].some((value) => value?.toLowerCase().includes(needle)))
      .filter((item) => status === 'all' || (status === 'online' ? item.online : !item.online))
      .filter((item) => group === 'all' || item.groupName === group)
      .filter((item) => Array.from(quickFilters).every((filter) => matchesQuickFilter(item, filter)))
      .sort((a, b) => Number(favorites.has(b.nodeId)) - Number(favorites.has(a.nodeId)) || Number(b.online) - Number(a.online) || a.name.localeCompare(b.name, 'ru'));
  }, [devices, favorites, group, matchesQuickFilter, query, quickFilters, status]);

  useEffect(() => { setPage(1); }, [query, status, group, quickFilters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selected = devices?.find((item) => item.nodeId === selectedNodeId);
  const online = (devices || []).filter((item) => item.online).length;

  const toggleQuickFilter = (filter: QuickFilter) => setQuickFilters((current) => {
    const next = new Set(current);
    if (next.has(filter)) next.delete(filter); else next.add(filter);
    return next;
  });

  const toggleFavorite = (nodeId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setFavorites((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const saveCurrentView = () => {
    const name = viewName.trim();
    if (!name) return;
    const next = [...savedViews, { id: crypto.randomUUID(), name, query, status, group, quickFilters: Array.from(quickFilters) }].slice(-12);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
    setSavedViews(next); setViewName(''); setSaveViewOpen(false);
  };

  const applySavedView = (view: SavedMeshView) => {
    setQuery(view.query); setStatus(view.status); setGroup(view.group); setQuickFilters(new Set(view.quickFilters));
  };

  const deleteSavedView = (id: string) => {
    const next = savedViews.filter((item) => item.id !== id);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next)); setSavedViews(next);
  };

  // Bulk operations helpers
  const toggleAll = () => {
    if (!filtered) return;
    const filteredIds = filtered.map((t) => t.nodeId);
    const allSelected = filteredIds.every(id => selectedNodeIds.has(id));
    if (allSelected) {
      // Deselect all filtered
      setSelectedNodeIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all filtered
      setSelectedNodeIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const toggleOne = (nodeId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const currentAction = BULK_ACTIONS.find(a => a.value === bulkAction);

  // Valid devices for current bulk action (selected + online if required)
  const validDevices = useMemo(() => {
    const selected = (devices || []).filter(t => selectedNodeIds.has(t.nodeId));
    return currentAction?.requiresOnline
      ? selected.filter(t => t.online)
      : selected;
  }, [devices, selectedNodeIds, currentAction]);

  const getConfirmation = (device: MeshDeviceDto) => {
    return device.name || device.hostname || device.nodeId;
  };

  const startJob = async (device: MeshDeviceDto): Promise<{ jobId?: number; error?: string }> => {
    const nodeId = device.nodeId;
    const confirmation = getConfirmation(device);

    try {
      if (bulkAction === 'update_medscreener') {
        const res = await createMeshCentralCommandJob(nodeId, 'update_medscreener', confirmation);
        return { jobId: res.data.data.id };
      }
      if (bulkAction === 'reboot') {
        const res = await createMeshCentralPowerOperation(nodeId, 'reboot', confirmation);
        return { jobId: res.data.data.id };
      }
      if (bulkAction === 'collect_support_bundle' || bulkAction === 'terminal_recovery') {
        const res = await createSupportJob({
          nodeId,
          scenarioId: bulkAction,
          confirmation: currentAction?.requiresConfirmation ? confirmation : undefined,
          idempotencyKey: crypto.randomUUID(),
        });
        return { jobId: res.data.data.id };
      }
      if (bulkAction === 'diagnostics') {
        await runMeshCentralDiagnostics(nodeId);
        return { jobId: -1 };
      }
      return { error: 'Неизвестное действие' };
    } catch (e) {
      return { error: getApiError(e) };
    }
  };

  const runBulk = async () => {
    if (!currentAction) return;

    if (validDevices.length === 0) {
      setError(currentAction.requiresOnline ? 'Нет выбранных онлайн устройств' : 'Нет выбранных устройств');
      return;
    }

    if (bulkAction === 'configuration_template') {
      setBulkConfigurationOpen(true);
      return;
    }

    setBulkRunning(true);
    setBulkModalOpen(true);
    setError('');
    setGlobalProgress(0);

    const initialStates = new Map<string, TerminalJobState>();
    validDevices.forEach(t => {
      initialStates.set(t.nodeId, {
        nodeId: t.nodeId,
        action: bulkAction,
        status: 'pending',
      });
    });
    setJobStates(initialStates);

    const concurrency = 3;
    let completed = 0;

    const runDeviceJob = async (device: typeof validDevices[number]) => {
      setJobStates(prev => {
        const next = new Map(prev);
        const state = next.get(device.nodeId);
        if (state) next.set(device.nodeId, { ...state, status: 'running', progress: 0 });
        return next;
      });

      const result = await startJob(device);
      completed++;
      setGlobalProgress(Math.round((completed / validDevices.length) * 100));

      if (result.error) {
        setJobStates(prev => {
          const next = new Map(prev);
          const state = next.get(device.nodeId);
          if (state) next.set(device.nodeId, { ...state, status: 'failed', error: result.error, progress: 100 });
          return next;
        });
      } else {
        setJobStates(prev => {
          const next = new Map(prev);
          const state = next.get(device.nodeId);
          if (state) next.set(device.nodeId, { ...state, status: 'completed', jobId: result.jobId, progress: 100 });
          return next;
        });
      }
    };

    for (let i = 0; i < validDevices.length; i += concurrency) {
      const batch = validDevices.slice(i, i + concurrency);
      await Promise.all(batch.map(runDeviceJob));
    }

    setBulkRunning(false);
  };

  const clearHistory = async () => {
    try {
      // Determine which API to call based on current action
      if (currentAction && ['collect_support_bundle', 'terminal_recovery'].includes(currentAction.value)) {
        await deleteSupportJobs(rows[0]?.nodeId || '');
      } else {
        await deleteMeshCentralCommandJobs(rows[0]?.nodeId || '');
      }
    } catch (e) {
      console.error('Failed to delete jobs:', e);
    }
    setJobStates(new Map());
    setGlobalProgress(0);
  };

  if (!bulkMode) return <AppLayout title="Терминалы" fullBleed>
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex min-h-16 shrink-0 items-center justify-between gap-4 border-b border-slate-800/10 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950 px-5 py-3 text-white">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 text-cyan-200"><Network className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2"><h1 className="truncate text-lg font-semibold">Парк терминалов</h1><span className="rounded-full bg-emerald-400/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-200">MeshCentral подключён</span></div>
            <p className="truncate text-xs text-slate-400">{devices?.length ?? 0} устройств · {online} в сети · {(devices?.length ?? 0) - online} требуют проверки</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setBulkMode(true)} className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"><Rows3 className="h-4 w-4" />Массовые действия</Button>
          <Button size="sm" onClick={refresh} disabled={refreshing} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"><RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />Обновить</Button>
        </div>
      </header>

      {error && <div role="alert" className="shrink-0 border-b border-destructive/25 bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>}

      <main className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden xl:grid-cols-[360px_minmax(0,1fr)]">
        <section className="flex min-h-0 flex-col overflow-hidden border-r border-border bg-card">
          <div className="shrink-0 border-b border-border p-3">
            <label className="relative block" htmlFor="mesh-workspace-search"><span className="sr-only">Поиск терминалов</span><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><input id="mesh-workspace-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти терминал, IP или организацию" className="h-10 w-full rounded-xl border border-input bg-background pl-9 pr-3 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <div className="mt-2 grid grid-cols-3 gap-1 rounded-xl bg-muted/70 p-1">
              {([['all', `Все ${devices?.length ?? 0}`], ['online', `В сети ${online}`], ['offline', `Offline ${(devices?.length ?? 0) - online}`]] as const).map(([value, label]) => <button key={value} onClick={() => setStatus(value)} className={cn('h-8 cursor-pointer rounded-lg px-2 text-xs font-semibold transition-all duration-200', status === value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>{label}</button>)}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <button onClick={() => setFiltersOpen((value) => !value)} className={cn('inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition-colors', filtersOpen || quickFilters.size ? 'bg-cyan-50 text-cyan-800' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}><SlidersHorizontal className="h-3.5 w-3.5" />Фильтры{quickFilters.size ? ` · ${quickFilters.size}` : ''}</button>
              <span className="text-[11px] text-muted-foreground">Найдено {filtered.length}</span>
            </div>
            {filtersOpen && <div className="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-xl border border-border bg-background p-2 shadow-sm">
              <FilterSelect label="Группа" value={group} onChange={setGroup} options={[['all', 'Все группы'], ...groups.map((item) => [item, item])]} className="block" />
              <div className="grid grid-cols-2 gap-1.5">{QUICK_FILTERS.map((filter) => <button key={filter.id} onClick={() => toggleQuickFilter(filter.id)} className={cn('cursor-pointer rounded-lg border px-2 py-1.5 text-left text-[11px] font-medium transition-colors', quickFilters.has(filter.id) ? 'border-cyan-300 bg-cyan-50 text-cyan-900' : 'border-border text-muted-foreground hover:bg-muted')}>{filter.label}</button>)}</div>
              <div className="flex items-center gap-2 border-t border-border pt-2"><Button size="sm" variant="outline" onClick={() => setSaveViewOpen(true)}><Save className="h-3.5 w-3.5" />Сохранить</Button><button onClick={() => { setQuickFilters(new Set()); setQuery(''); setStatus('all'); setGroup('all'); }} className="cursor-pointer text-xs font-semibold text-destructive hover:underline">Сбросить</button></div>
              {savedViews.length > 0 && <div className="flex flex-wrap gap-1.5">{savedViews.map((view) => <span key={view.id} className="inline-flex overflow-hidden rounded-lg border border-border"><button onClick={() => applySavedView(view)} className="cursor-pointer px-2 py-1 text-[11px] hover:bg-muted">{view.name}</button><button onClick={() => deleteSavedView(view.id)} className="cursor-pointer border-l border-border px-1.5 text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button></span>)}</div>}
            </div>}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {!devices ? <LoadingState /> : rows.length === 0 ? <div className="flex h-full min-h-48 flex-col items-center justify-center text-center"><Search className="h-7 w-7 text-muted-foreground" /><p className="mt-2 text-sm font-semibold">Ничего не найдено</p><p className="text-xs text-muted-foreground">Измените поиск или фильтр</p></div> : <div className="space-y-1">{rows.map((item) => <button key={item.nodeId} onClick={() => setSelectedNodeId(item.nodeId)} className={cn('group flex w-full cursor-pointer items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-all duration-200', selectedNodeId === item.nodeId ? 'border-cyan-200 bg-cyan-50 shadow-sm' : 'hover:bg-muted/70')}>
              <span className={cn('relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors', item.online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}><Server className="h-4 w-4" /><span className={cn('absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-card', item.online ? 'bg-emerald-500' : 'bg-slate-400')} /></span>
              <span className="min-w-0 flex-1"><span className="flex items-center gap-1.5"><span className="block truncate text-sm font-semibold text-foreground">{item.name || 'Без имени'}</span>{deviceNeedsAttention(item) && <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />}</span><span className="mt-0.5 block truncate font-mono text-[11px] text-muted-foreground">{item.hostname || item.ip || item.nodeId}</span></span>
              <span onClick={(event) => toggleFavorite(item.nodeId, event)} className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors', favorites.has(item.nodeId) ? 'text-amber-500' : 'text-muted-foreground/30 opacity-0 group-hover:opacity-100')} role="button" aria-label="Избранное"><Star className={cn('h-4 w-4', favorites.has(item.nodeId) && 'fill-current')} /></span>
            </button>)}</div>}
          </div>
          <footer className="flex shrink-0 items-center justify-between border-t border-border px-3 py-2"><span className="text-[11px] text-muted-foreground">{filtered.length ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)}` : '0'} из {filtered.length}</span><div className="flex items-center gap-1"><Button variant="ghost" size="icon-sm" disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Назад"><ChevronLeft className="h-4 w-4" /></Button><span className="min-w-12 text-center text-xs font-semibold">{page}/{totalPages}</span><Button variant="ghost" size="icon-sm" disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Вперёд"><ChevronRight className="h-4 w-4" /></Button></div></footer>
        </section>

        <section className="min-h-0 overflow-hidden bg-card">
          {selected ? <MeshDevicePanel key={selected.nodeId} device={selected} variant="workspace" /> : <div className="flex h-full flex-col items-center justify-center p-8 text-center"><span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700"><Sparkles className="h-7 w-7" /></span><h2 className="mt-4 text-lg font-semibold">Выберите терминал</h2><p className="mt-1 max-w-sm text-sm text-muted-foreground">Состояние, оборудование и действия откроются здесь — без новых страниц и окон.</p></div>}
        </section>
      </main>
    </div>
    <Modal open={saveViewOpen} onClose={() => setSaveViewOpen(false)} title="Сохранить фильтр" maxWidth="max-w-md" footer={<><Button variant="ghost" onClick={() => setSaveViewOpen(false)}>Отмена</Button><Button onClick={saveCurrentView} disabled={!viewName.trim()}>Сохранить</Button></>}>
      <Input label="Название" value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="Например, проблемные терминалы" autoFocus />
    </Modal>
  </AppLayout>;

  return <AppLayout title="MeshCentral">
    <PageHeader
      title="MeshCentral"
      description="Терминалы, состояние оборудования и безопасное удалённое управление."
      action={<div className="flex items-center gap-2"><Button variant={bulkPanelOpen ? 'default' : 'outline'} onClick={() => { setBulkPanelOpen((value) => !value); setSelectedNodeIds(new Set()); }}><CheckSquare className="h-4 w-4" />{bulkPanelOpen ? 'Завершить выбор' : 'Массовые действия'}</Button><Button onClick={refresh} disabled={refreshing}><RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />Обновить</Button></div>}
    />

    {error && <div role="alert" className="mb-5 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

    <section className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-border bg-card px-5 py-3 shadow-sm">
      <span className="inline-flex items-center gap-2 text-sm font-semibold"><Network className="h-4 w-4 text-cyan-700" />MeshCentral подключён</span>
      <span className="text-sm text-muted-foreground"><strong className="text-foreground">{devices?.length ?? 0}</strong> терминалов</span>
      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><Wifi className="h-4 w-4" /><strong>{online}</strong> в сети</span>
      <span className="inline-flex items-center gap-1.5 text-sm text-amber-700"><WifiOff className="h-4 w-4" /><strong>{(devices?.length ?? 0) - online}</strong> offline</span>
    </section>

    <div>
      <section className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row">
            <label className="relative min-w-0 flex-1" htmlFor="mesh-search"><span className="sr-only">Поиск устройств</span><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><input id="mesh-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Имя, hostname, IP, ОС, группа или Node ID" className="h-11 w-full rounded-xl border border-input bg-white pl-10 pr-3 text-sm text-slate-950 outline-none transition-colors placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <div className="grid grid-cols-2 gap-2 lg:flex">
              <FilterSelect label="Статус" value={status} onChange={(value) => setStatus(value as StatusFilter)} options={[['all', 'Все статусы'], ['online', 'Online'], ['offline', 'Offline']]} />
              <FilterSelect label="Группа" value={group} onChange={setGroup} options={[['all', 'Все группы'], ...groups.map((item) => [item, item])]} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"><span>Найдено: <strong className="text-foreground">{filtered.length}</strong></span><span>Обновление каждые 30 секунд</span></div>
          <div className="mt-4 border-t border-border/70 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><SlidersHorizontal className="h-3.5 w-3.5" />Быстрые фильтры</span>
              {QUICK_FILTERS.map((filter) => <button key={filter.id} onClick={() => toggleQuickFilter(filter.id)} className={cn('cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors', quickFilters.has(filter.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground')}>{filter.label}</button>)}
              {(quickFilters.size > 0 || query || status !== 'all' || group !== 'all') && <button onClick={() => { setQuickFilters(new Set()); setQuery(''); setStatus('all'); setGroup('all'); }} className="cursor-pointer px-2 py-1.5 text-xs font-semibold text-destructive hover:underline">Сбросить</button>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setSaveViewOpen(true)}><Save className="h-3.5 w-3.5" />Сохранить вид</Button>
              {savedViews.map((view) => <span key={view.id} className="inline-flex overflow-hidden rounded-lg border border-border bg-card"><button onClick={() => applySavedView(view)} className="cursor-pointer px-2.5 py-1.5 text-xs font-medium hover:bg-muted">{view.name}</button><button onClick={() => deleteSavedView(view.id)} className="cursor-pointer border-l border-border px-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={`Удалить вид ${view.name}`}><X className="h-3 w-3" /></button></span>)}
              {newestFleetVersion && <span className="ml-auto text-[11px] text-muted-foreground">Новейшая версия в парке: <strong className="font-mono text-foreground">{newestFleetVersion}</strong></span>}
            </div>
          </div>
        </div>

        {/* Bulk Operations Panel */}
        {bulkPanelOpen && <div className="border-b border-cyan-200 bg-cyan-50/40 p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-4 rounded-xl border border-cyan-200 bg-card p-4 shadow-sm">
            <div className="flex h-10 min-w-10 items-center justify-center rounded-xl bg-cyan-100 font-bold text-cyan-800">{selectedNodeIds.size}</div>
            <div className="flex items-center gap-3 min-w-[280px] max-w-[400px]">
              <Select value={bulkAction} onChange={e => { setBulkAction(e.target.value as BulkAction); setJobStates(new Map()); setGlobalProgress(0); }}>
                <option value="">Выберите действие</option>
                {BULK_ACTIONS.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </Select>
            </div>
            {currentAction && (
              <div className="flex-1 min-w-[200px] text-sm text-muted-foreground">
                {currentAction.description}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clearHistory}
                disabled={jobStates.size === 0}
                className="text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Очистить историю
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); runBulk(); }}
                disabled={bulkRunning || selectedNodeIds.size === 0 || !currentAction}
                className="ml-auto"
              >
                {bulkRunning ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                    Выполняется... {globalProgress}%
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Запустить на {selectedNodeIds.size} устройствах
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Global Progress */}
          {bulkRunning && globalProgress > 0 && globalProgress < 100 && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
              <div className="flex items-center justify-between gap-4 mb-2">
                <span className="text-sm font-semibold">Общий прогресс: {globalProgress}%</span>
                <span className="text-xs text-muted-foreground">
                  Завершено: {Array.from(jobStates.values()).filter(s => s.status === 'completed' || s.status === 'failed').length} / {jobStates.size}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${globalProgress}%` }} />
              </div>
            </div>
          )}

          {/* Bulk Operation Progress Modal */}
          {bulkModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBulkModalOpen(false)}>
              <div className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-2xl border border-border bg-card shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-border p-4 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
                    <div>
                      <h3 className="font-semibold">Массовая операция: {currentAction?.label}</h3>
                      <p className="text-xs text-muted-foreground">{globalProgress}% · {Array.from(jobStates.values()).filter(s => s.status === 'completed' || s.status === 'failed').length} / {jobStates.size} завершено</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setBulkModalOpen(false)}
                    className="text-destructive hover:bg-destructive/10"
                    aria-label="Закрыть"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-3">
                    {validDevices.map((device) => {
                      const jobState = jobStates.get(device.nodeId);
                      const isPending = jobState?.status === 'pending';
                      const isRunning = jobState?.status === 'running';
                      const isCompleted = jobState?.status === 'completed';
                      const isFailed = jobState?.status === 'failed';

                      return (
                        <div key={device.nodeId} className="rounded-xl border border-border p-4 transition-colors bg-muted/30">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', device.online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                                <Server className="h-4 w-4" />
                              </span>
                              <div className="min-w-0">
                                <p className="font-medium truncate">{device.name || 'Без имени'}</p>
                                <p className="text-xs text-muted-foreground font-mono truncate">{device.hostname || device.nodeId}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isRunning && <LoaderCircle className="h-5 w-5 animate-spin text-primary" />}
                              {isPending && <span className="text-sm text-muted-foreground px-2 py-1 rounded bg-muted">В очереди</span>}
                              {isCompleted && <span className="text-sm text-success px-2 py-1 rounded bg-emerald-50"><CheckCircle className="h-4 w-4 inline" /> Передано в центр задач</span>}
                              {isFailed && <span className="text-sm text-destructive px-2 py-1 rounded bg-rose-50"><AlertCircle className="h-4 w-4 inline" /> Ошибка</span>}
                            </div>
                          </div>
                          {isRunning && jobState.progress !== undefined && (
                            <div className="h-2 overflow-hidden rounded-full bg-muted">
                              <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${jobState.progress}%` }} />
                            </div>
                          )}
                          {isFailed && jobState.error && (
                            <p className="mt-2 text-xs text-destructive font-mono break-all">{jobState.error}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>}

        {!devices ? <LoadingState /> : filtered.length === 0 ? <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center"><Search className="h-8 w-8 text-muted-foreground" /><h3 className="mt-3 font-semibold">Устройства не найдены</h3><p className="mt-1 text-sm text-muted-foreground">Измените поисковый запрос или фильтры.</p></div> : <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-muted/45 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"><tr>{bulkPanelOpen && <th className="w-12 px-2 py-3"><button onClick={toggleAll} className="flex h-6 w-6 items-center justify-center rounded border border-border bg-background" aria-label="Выбрать все">
                  {rows && selectedNodeIds.size === rows.length
                    ? <CheckSquare className="h-4 w-4 text-primary" />
                    : <Square className="h-4 w-4 text-muted-foreground" />
                  }
                </button></th>}<th className="px-5 py-3">Устройство</th><th className="px-4 py-3">Статус</th><th className="px-4 py-3">Сеть</th><th className="px-4 py-3">Группа / ОС</th><th className="px-4 py-3">Mesh Agent</th>{bulkPanelOpen && <th className="w-[200px] px-4 py-3">Операция</th>}</tr></thead>
              <tbody className="divide-y divide-border">
                {rows.map((item) => {
                  const jobState = jobStates.get(item.nodeId);
                  const isPending = jobState?.status === 'pending';
                  const isRunning = jobState?.status === 'running';
                  const isCompleted = jobState?.status === 'completed';
                  const isFailed = jobState?.status === 'failed';

                  return (<tr key={item.nodeId} onClick={(event) => { if (bulkPanelOpen) toggleOne(item.nodeId, event); else setSelectedNodeId(item.nodeId); }} className={cn('cursor-pointer transition-colors hover:bg-cyan-50/70', selectedNodeIds.has(item.nodeId) && bulkPanelOpen && 'bg-cyan-50 ring-1 ring-inset ring-cyan-600/20')}>
                    {bulkPanelOpen && <td className="w-12 px-2 py-3.5"><button onClick={(e) => { e.stopPropagation(); toggleOne(item.nodeId); }} className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border bg-background" aria-label="Выбрать устройство">
                      {selectedNodeIds.has(item.nodeId)
                        ? <CheckSquare className="h-4 w-4 text-primary" />
                        : <Square className="h-4 w-4 text-muted-foreground" />
                      }
                    </button></td>}
                    <td className="px-5 py-3.5"><div className="flex max-w-[320px] items-center gap-2"><button onClick={(event) => toggleFavorite(item.nodeId, event)} className={cn('flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-amber-50', favorites.has(item.nodeId) ? 'text-amber-500' : 'text-muted-foreground/50')} aria-label={favorites.has(item.nodeId) ? 'Убрать из избранного' : 'Добавить в избранное'}><Star className={cn('h-4 w-4', favorites.has(item.nodeId) && 'fill-current')} /></button><button onClick={(event) => { event.stopPropagation(); if (bulkPanelOpen) toggleOne(item.nodeId); else setSelectedNodeId(item.nodeId); }} className="flex min-w-0 cursor-pointer items-center gap-3 text-left focus-visible:rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', item.online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}><Server className="h-4 w-4" /></span><span className="min-w-0"><span className="flex items-center gap-2"><span className="block truncate font-semibold text-foreground">{item.name || 'Без имени'}</span>{deviceNeedsAttention(item) && <span className="h-2 w-2 shrink-0 rounded-full bg-destructive" title="Требует внимания" />}</span><span className="block truncate font-mono text-[11px] text-muted-foreground">{item.hostname || item.nodeId}</span></span></button></div></td>
                    <td className="px-4 py-3.5"><StatusBadge label={item.online ? 'Online' : 'Offline'} variant={item.online ? 'success' : 'default'} live={item.online} /></td>
                    <td className="px-4 py-3.5"><p className="font-mono text-xs text-foreground">{item.ip || '—'}</p><p className="mt-1 text-[11px] text-muted-foreground">conn: {item.connectionMask}</p></td>
                    <td className="max-w-[220px] px-4 py-3.5"><p className="truncate text-xs font-medium">{item.groupName || 'Без группы'}</p><p className="mt-1 truncate text-[11px] text-muted-foreground">{item.os || 'ОС не определена'}</p></td>
                    <td className="px-4 py-3.5"><p className="font-mono text-xs text-foreground">v{item.agentVersion ?? '—'}</p><p className="mt-1 text-[11px] text-muted-foreground">caps: {item.agentCapabilities ?? '—'}</p></td>
                    {bulkPanelOpen && <td className="w-[200px] px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        {jobState && (
                          <>
                            {isRunning && <LoaderCircle className="h-4 w-4 animate-spin text-primary" />}
                            {isPending && <span className="text-xs text-muted-foreground">В очереди</span>}
                            {isCompleted && <CheckCircle className="h-4 w-4 text-success" />}
                            {isFailed && (
                              <div className="flex items-center gap-1 text-destructive" title={jobState.error}>
                                <AlertCircle className="h-4 w-4" />
                                <span className="text-xs truncate max-w-[150px]">{jobState.error}</span>
                              </div>
                            )}
                            {(isRunning || isPending) && jobState.progress !== undefined && (
                              <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-muted">
                                <div className="h-full rounded-full bg-primary" style={{ width: `${jobState.progress}%` }} />
                              </div>
                            )}
                          </>
                        )}
                        {!jobState && !selectedNodeIds.has(item.nodeId) && <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </td>}
                  </tr>);
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm"><p className="text-xs text-muted-foreground">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} из {filtered.length}</p><div className="flex items-center gap-2"><Button variant="outline" size="icon-sm" aria-label="Предыдущая страница" disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft className="h-4 w-4" /></Button><span className="min-w-16 text-center text-xs font-semibold">{page} / {totalPages}</span><Button variant="outline" size="icon-sm" aria-label="Следующая страница" disabled={page === totalPages} onClick={() => setPage((value) => value + 1)}><ChevronRight className="h-4 w-4" /></Button></div></div>
        </>}
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-2 backdrop-blur-md sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Управление устройством ${selected.name || selected.hostname || selected.nodeId}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedNodeId(undefined);
          }}
        >
          <div className="flex h-[calc(100vh-32px)] w-[min(1600px,calc(100vw-32px))] flex-col overflow-hidden rounded-3xl border border-white/15 bg-background shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-5 sm:px-7">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">Управление терминалом</p>
                <p className="truncate text-base font-semibold text-foreground">{selected.name || selected.hostname || 'Без имени'}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(meshDevicePath(selected.nodeId), { state: { device: selected } })}
                >
                  Открыть отдельно
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setSelectedNodeId(undefined)} aria-label="Закрыть карточку терминала">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden bg-muted/25">
              <MeshDevicePanel device={selected} variant="workspace" />
            </div>
          </div>
        </div>
      )}
      {bulkConfigurationOpen && <MeshBulkConfiguration devices={validDevices} onClose={() => setBulkConfigurationOpen(false)} />}
      <Modal open={saveViewOpen} onClose={() => setSaveViewOpen(false)} title="Сохранить фильтр" maxWidth="max-w-md" footer={<><Button variant="ghost" onClick={() => setSaveViewOpen(false)}>Отмена</Button><Button onClick={saveCurrentView} disabled={!viewName.trim()}>Сохранить</Button></>}>
        <Input label="Название" value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="Например, проблемные терминалы" autoFocus />
        <p className="mt-3 text-xs leading-5 text-muted-foreground">Будут сохранены текущий поиск, статус, группа и быстрые фильтры на этом компьютере.</p>
      </Modal>
    </div>
  </AppLayout>;
};

const deviceNeedsAttention = (item: MeshDeviceDto) => !item.online
  || item.telemedHeartbeatOnline === false
  || (item.diagnostics?.disk?.usedPercent || 0) >= 90
  || (item.diagnostics?.telemedStatus != null && item.diagnostics.telemedStatus !== 'running')
  || item.latestPrintStatus === 'failed'
  || item.latestUpdateStatus === 'failed'
  || Boolean(item.latestErrorMessage);

const FilterSelect = ({ label, value, onChange, options, className }: { label: string; value: string; onChange: (value: string) => void; options: string[][]; className?: string }) => <label className={cn('min-w-0', className)}><span className="sr-only">{label}</span><select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className="h-11 w-full rounded-xl border border-input bg-white px-3 text-sm text-slate-950 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 lg:max-w-44">{options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}</select></label>;
